import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { Wrench, AlertCircle, Clock, CheckCircle, Plus, Lightbulb, Star, MessageSquare, User, Calendar, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import TroubleshootingTips from '../components/TroubleshootingTips';
import MaintenanceSurvey from '../components/MaintenanceSurvey';

/**
 * Maintenance Page - Uses Unified Cases API
 * 
 * This page displays maintenance requests which are now stored as cases
 * with case_type='maintenance' or other maintenance-related types.
 * 
 * API: /api/cases with category=maintenance filter
 */
export default function Maintenance() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [showSurvey, setShowSurvey] = useState(null);
  const [surveyDismissed, setSurveyDismissed] = useState({});
  
  const [formData, setFormData] = useState({
    tenancy_id: '',
    property_id: '',
    title: '',
    description: '',
    maintenance_category: 'plumbing',
    priority: 'medium'
  });

  const isTenant = user?.role === 'tenant';
  const isLandlord = user?.role === 'landlord';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Fetch maintenance cases from unified API
      const casesRes = await api.get('/cases', { 
        params: { category: 'maintenance' } 
      });
      
      // Transform cases data to match existing component expectations
      const maintenanceCases = casesRes.data.cases || casesRes.data || [];
      setRequests(maintenanceCases.map(c => ({
        id: c.id,
        case_number: c.case_number,
        title: c.title,
        description: c.description,
        category: c.maintenance_category || c.case_type,
        urgency: c.priority,
        status: c.status,
        created_at: c.created_at,
        property_id: c.property_id,
        tenancy_id: c.tenancy_id,
        assigned_to: c.assigned_to,
        contractor_name: c.contractor_name,
        timeline: c.timeline || []
      })));
      
      // Try to fetch tenancies for the form dropdown (may fail for some users)
      try {
        const tenanciesRes = await api.get('/tenancies');
        const activeT = (tenanciesRes.data || []).filter(t => t.status === 'active');
        setTenancies(activeT);
        
        // Extract unique properties from tenancies for landlord dropdown
        if (isLandlord) {
          const uniqueProps = [...new Map(activeT.map(t => [t.property_id, { 
            id: t.property_id, 
            address: t.property_address || `Property ${t.property_id?.slice(0,8)}` 
          }])).values()];
          setProperties(uniqueProps);
        }
      } catch (tenancyError) {
        console.log('Tenancies not available - form may have limited options');
        // Try to fetch properties directly for landlords
        if (isLandlord) {
          try {
            const propsRes = await api.get('/properties');
            const propsList = Array.isArray(propsRes.data) ? propsRes.data : [];
            setProperties(propsList.map(p => ({
              id: p.id,
              address: p.address_line_1 || `Property ${p.id?.slice(0,8)}`
            })));
          } catch {
            // Properties also not available - user can still view cases
          }
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load maintenance requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show troubleshooting tips before submitting
    if (!showTips && (formData.title || formData.description)) {
      setShowTips(true);
      return;
    }
    
    await submitRequest();
  };

  const submitRequest = async () => {
    try {
      // Create maintenance case using unified API
      const payload = {
        case_type: 'maintenance',
        title: formData.title,
        description: formData.description,
        maintenance_category: formData.maintenance_category,
        priority: formData.priority,
        tenancy_id: formData.tenancy_id || undefined,
        property_id: formData.property_id || undefined
      };
      
      await api.post('/cases/maintenance', payload);
      toast.success('Maintenance request submitted!');
      setShowNewRequest(false);
      setShowTips(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleTipsProceed = async () => {
    await submitRequest();
  };

  const handleTipsResolved = () => {
    toast.success('Great! Glad the tips helped resolve your issue.');
    setShowNewRequest(false);
    setShowTips(false);
    resetForm();
  };

  const handleSurveyComplete = (requestId) => {
    setShowSurvey(null);
    toast.success('Thank you for your feedback!');
  };

  const handleSurveySkip = (requestId) => {
    setSurveyDismissed(prev => ({ ...prev, [requestId]: true }));
    setShowSurvey(null);
  };

  const resetForm = () => {
    setFormData({
      tenancy_id: '',
      property_id: '',
      title: '',
      description: '',
      maintenance_category: 'plumbing',
      priority: 'medium'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
      case 'completed': 
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress':
      case 'assigned':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'under_review':
      case 'awaiting_approval':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default: 
        return <Wrench className="w-5 h-5 text-slate-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
      case 'completed': 
        return 'bg-green-100 text-green-800';
      case 'in_progress':
      case 'assigned': 
        return 'bg-blue-100 text-blue-800';
      case 'under_review':
      case 'awaiting_approval': 
        return 'bg-yellow-100 text-yellow-800';
      case 'escalated':
        return 'bg-red-100 text-red-800';
      default: 
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
      case 'emergency': 
        return 'bg-red-500 text-white';
      case 'high': 
        return 'bg-orange-500 text-white';
      case 'medium': 
        return 'bg-yellow-500 text-white';
      default: 
        return 'bg-slate-500 text-white';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'plumbing': return '🔧';
      case 'electrical': return '⚡';
      case 'heating': return '🔥';
      case 'appliances': return '🔌';
      case 'structural': return '🏗️';
      case 'security': return '🔒';
      default: return '🛠️';
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl" data-testid="maintenance-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-display font-bold text-slate-900 mb-2">Maintenance Requests</h1>
            <p className="text-lg text-slate-600">
              {isTenant ? 'Report and track property issues' : 'Manage maintenance requests for your properties'}
            </p>
          </div>
          {(isTenant && tenancies.length > 0) || isLandlord ? (
            <Button onClick={() => setShowNewRequest(true)} data-testid="btn-new-request">
              <Plus className="w-5 h-5 mr-2" />
              New Request
            </Button>
          ) : null}
        </div>

        {/* New Request Dialog */}
        <Dialog open={showNewRequest} onOpenChange={(open) => {
          setShowNewRequest(open);
          if (!open) {
            setShowTips(false);
            resetForm();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-display font-bold">
                {showTips ? 'Before You Submit...' : 'Submit Maintenance Request'}
              </DialogTitle>
            </DialogHeader>
            
            {showTips ? (
              <div className="mt-4" data-testid="troubleshooting-tips-section">
                <TroubleshootingTips
                  title={formData.title}
                  description={formData.description}
                  onProceed={handleTipsProceed}
                  onResolved={handleTipsResolved}
                />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 mt-6" data-testid="maintenance-request-form">
                {/* Tenancy/Property Selection */}
                {isTenant && (
                  <div>
                    <Label>Tenancy</Label>
                    <Select 
                      value={formData.tenancy_id} 
                      onValueChange={(value) => {
                        const tenancy = tenancies.find(t => t.id === value);
                        setFormData({
                          ...formData, 
                          tenancy_id: value,
                          property_id: tenancy?.property_id || ''
                        });
                      }} 
                      required
                    >
                      <SelectTrigger data-testid="select-tenancy">
                        <SelectValue placeholder="Select tenancy" />
                      </SelectTrigger>
                      <SelectContent>
                        {tenancies.map(tenancy => (
                          <SelectItem key={tenancy.id} value={tenancy.id}>
                            {tenancy.property_address || `Tenancy #${tenancy.id.slice(0, 8)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {isLandlord && properties.length > 0 && (
                  <div>
                    <Label>Property</Label>
                    <Select 
                      value={formData.property_id} 
                      onValueChange={(value) => setFormData({...formData, property_id: value})}
                    >
                      <SelectTrigger data-testid="select-property">
                        <SelectValue placeholder="Select property (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map(prop => (
                          <SelectItem key={prop.id} value={prop.id}>
                            {prop.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Brief description of the issue"
                    required
                    data-testid="input-title"
                  />
                </div>

                <div>
                  <Label>Category</Label>
                  <Select 
                    value={formData.maintenance_category} 
                    onValueChange={(value) => setFormData({...formData, maintenance_category: value})}
                  >
                    <SelectTrigger data-testid="select-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plumbing">🔧 Plumbing</SelectItem>
                      <SelectItem value="electrical">⚡ Electrical</SelectItem>
                      <SelectItem value="heating">🔥 Heating</SelectItem>
                      <SelectItem value="appliances">🔌 Appliances</SelectItem>
                      <SelectItem value="structural">🏗️ Structural</SelectItem>
                      <SelectItem value="security">🔒 Security</SelectItem>
                      <SelectItem value="other">🛠️ Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => setFormData({...formData, priority: value})}
                  >
                    <SelectTrigger data-testid="select-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - Can wait a week</SelectItem>
                      <SelectItem value="medium">Medium - Within a few days</SelectItem>
                      <SelectItem value="high">High - Within 24 hours</SelectItem>
                      <SelectItem value="critical">Emergency - Immediate attention</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={6}
                    placeholder="Provide detailed information about the issue..."
                    required
                    data-testid="input-description"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button type="submit" className="flex-1" data-testid="btn-submit-request">
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Check Tips & Submit
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowNewRequest(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">Loading maintenance requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <Wrench className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold mb-2">No maintenance requests</h3>
            <p className="text-slate-600 mb-6">
              {isTenant 
                ? (tenancies.length === 0 
                    ? 'You need an active tenancy to submit requests' 
                    : 'Submit your first maintenance request') 
                : 'Maintenance requests from your tenants will appear here'}
            </p>
            {((isTenant && tenancies.length > 0) || isLandlord) && (
              <Button onClick={() => setShowNewRequest(true)} data-testid="btn-empty-new-request">
                <Plus className="w-4 h-4 mr-2" />
                Submit Request
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <div 
                key={request.id} 
                className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow" 
                data-testid={`maintenance-request-${request.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    {getStatusIcon(request.status)}
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2 flex-wrap">
                        <h3 className="text-xl font-semibold">{request.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getPriorityColor(request.urgency)} uppercase`}>
                          {request.urgency}
                        </span>
                        {request.case_number && (
                          <span className="px-2 py-1 rounded bg-slate-100 text-slate-600 text-xs font-mono">
                            {request.case_number}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-700 mb-3">{request.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          {getCategoryIcon(request.category)} {request.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Reported: {formatDate(request.created_at)}
                        </span>
                        {request.assigned_to && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            Assigned
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                    {request.status?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                
                {/* Survey prompt for completed requests (tenant only) */}
                {isTenant && (request.status === 'completed' || request.status === 'resolved') && 
                 !surveyDismissed[request.id] && showSurvey !== request.id && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-blue-600" />
                      <span className="text-blue-800 font-medium">How was the service? Leave feedback</span>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => setShowSurvey(request.id)}
                      data-testid={`btn-leave-feedback-${request.id}`}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Leave Feedback
                    </Button>
                  </div>
                )}
                
                {/* Inline Survey */}
                {showSurvey === request.id && (
                  <div className="mt-4" data-testid={`survey-section-${request.id}`}>
                    <MaintenanceSurvey
                      maintenanceRequestId={request.id}
                      maintenanceTitle={request.title}
                      contractorName={request.contractor_name}
                      onComplete={() => handleSurveyComplete(request.id)}
                      onSkip={() => handleSurveySkip(request.id)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
